/**
 * When to use a generic purchase-order service stock keeping unit.
 * Shown next to the line picker on hub purchase orders and contract-manufacturer purchase orders.
 */
const WHEN_BY_SKU: Record<string, string> = {
  fee: "Use for an extra charge that is not a part, and is not shipping or tariff. Name the charge in the line description.",
  tooling:
    "Use for reusable tooling, such as a fixture, die, or mold you will use again. Do not use it as a stand-in part number. Name the tool in the line description.",
  prototype:
    "Use for one-off prototype work that will not be stocked under its own part number. Name the work in the line description.",
  "shipping-i": "Use for international freight charged on this order.",
  "shipping-d": "Use for domestic freight charged on this order.",
  "shipping-o": "Use for outbound shipping and handling. For freight you pay to receive parts, use Shipping-D or Shipping-I.",
  shipping: "Use when the charge is freight and domestic or international is not the right split. Prefer Shipping-D or Shipping-I.",
  tariff: "Use for tariff or import tax charged on this order.",
  tax: "Use for sales tax on this order. For import duty, use Tariff.",
  discount: "Use for a price reduction on this order, not for a part.",
  "value-add": "Use for a value-add service the supplier performs, not for a part you will stock.",
  "restock fee": "Use when the supplier charges a restocking fee.",
  testing: "Use when the supplier is charging for a test. Say what was tested in the line description.",
  calibration: "Use when paying to calibrate a meter or tool, not to buy the tool.",
  coating: "Use for a finish service, such as trigger plate coating, not for the part itself.",
  manufacturing: "Use for a contract-manufacturer charge that is not a finished-goods part.",
};

const TSH_NOT_INVENTORY_VALUE =
  "Shipping-D, Shipping-I, and Tax are normally handled on a TSH. Put them on a purchase order or a contract-manufacturer purchase order only when they do not add to the value of an inventory item.";

const ROLL_INTO_ITEM_COST =
  "If this adds to the ongoing cost of an item, roll it into that item's cost. Do not add it as a separate line.";

const VALUE_ADD_CM_ONLY = "Use Value-add only on a contract-manufacturer purchase order.";

export type PurchaseOrderServiceSkuSurface = "hub" | "cm";

export function purchaseOrderServiceSkuWhen(
  sku: string | null | undefined,
  surface: PurchaseOrderServiceSkuSurface = "hub",
): string | null {
  const key = (sku ?? "").trim().toLowerCase();
  if (key === "") return null;
  const when = WHEN_BY_SKU[key];
  if (when == null) return null;
  const parts = [when];
  if (key === "shipping-i" || key === "shipping-d" || key === "tax") {
    parts.push(TSH_NOT_INVENTORY_VALUE);
  }
  if (key === "value-add") {
    parts.push(VALUE_ADD_CM_ONLY);
    if (surface === "hub") {
      return `${parts.join(" ")} ${ROLL_INTO_ITEM_COST}`;
    }
  }
  parts.push(ROLL_INTO_ITEM_COST);
  return parts.join(" ");
}

export function PurchaseOrderServiceSkuWhen({
  sku,
  surface = "hub",
}: {
  sku: string | null | undefined;
  surface?: PurchaseOrderServiceSkuSurface;
}): JSX.Element | null {
  const text = purchaseOrderServiceSkuWhen(sku, surface);
  if (text == null) return null;
  return <p className="mt-1 max-w-sm text-xs font-sans font-normal normal-case tracking-normal text-slate-600">{text}</p>;
}
